/**
 * Per-project custom domains UI.
 *
 * A domain in Doable is a triple of (hostname, prod server, DNS state).
 * Adding a domain provisions a fresh Hetzner server on the tenant's account
 * (cost-incurring) — so the Add modal calls that out and the Remove confirm
 * spells out that termination is what stops the meter.
 *
 * Lifecycle (matches services/domains.ts):
 *   configuring  — added; server provisioning in flight (<2 min)
 *   dns_pending  — IP allocated; awaiting A record
 *   cert_pending — DNS verified; cert issuance is manual for Beta
 *   live         — DNS + cert OK
 *   suspended    — admin paused or termination failed; needs ops attention
 */
import { useEffect, useMemo, useState } from 'react'
import { Copy, Globe, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  api,
  type ApiError,
  type DomainDto,
  type HetznerServerTypeDto,
  type HetznerSnapshotDto,
} from '../lib/api'
import { Badge, StatusBox, type BadgeVariant } from './ui/badge'
import { Button } from './ui/button'
import { Dialog } from './ui/dialog'
import { FloatingInput, FloatingSelect } from './ui/input'
import { EmptyState } from './ui/empty-state'

type Props = {
  projectId: string
  accessToken: string
  isAdmin: boolean
  refreshKey: number
}

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  configuring: 'warning',
  dns_pending: 'warning',
  cert_pending: 'warning',
  live: 'success',
  suspended: 'destructive',
}

const STATUS_LABEL: Record<string, string> = {
  configuring: 'Setting up server',
  dns_pending: 'Waiting for DNS',
  cert_pending: 'Awaiting certificate',
  live: 'Live',
  suspended: 'Suspended',
}

export function Domains({ projectId, accessToken, isAdmin, refreshKey }: Props) {
  const [domains, setDomains] = useState<DomainDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [checkingId, setCheckingId] = useState<string | null>(null)
  const [checkResults, setCheckResults] = useState<
    Record<string, { resolved: boolean; expected: string | null; actual: string[]; matched: boolean }>
  >({})

  useEffect(() => {
    let cancelled = false
    api.domains
      .list(accessToken, projectId)
      .then((res) => {
        if (!cancelled) {
          setDomains(res.domains)
          setError(null)
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) setError(err.title ?? 'Failed to load domains')
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, projectId, refreshKey, reloadKey])

  async function handleCheckDns(domainId: string) {
    setCheckingId(domainId)
    try {
      const res = await api.domains.checkDns(accessToken, domainId)
      setCheckResults((prev) => ({ ...prev, [domainId]: res }))
      // Status may have transitioned (dns_pending → cert_pending) — refresh.
      setReloadKey((k) => k + 1)
    } catch (err) {
      const e = err as ApiError
      setError(e.title ?? 'DNS check failed')
    } finally {
      setCheckingId(null)
    }
  }

  async function handleRemove(domain: DomainDto) {
    const ok = window.confirm(
      `Remove ${domain.domain}?\n\n` +
        `This will TERMINATE the prod server on Hetzner (the only thing that stops the meter on your bill). ` +
        `If termination fails, the domain will be marked suspended so the server is never silently abandoned. ` +
        `This cannot be undone.`,
    )
    if (!ok) return
    setRemovingId(domain.id)
    try {
      await api.domains.remove(accessToken, domain.id)
      setReloadKey((k) => k + 1)
    } catch (err) {
      const e = err as ApiError
      setError(e.title ?? 'Failed to remove domain')
    } finally {
      setRemovingId(null)
    }
  }

  if (error && !domains) {
    return (
      <div className="p-4">
        <StatusBox tone="destructive">{error}</StatusBox>
      </div>
    )
  }
  if (domains === null) return <p className="p-5 text-sm text-muted-foreground">Loading domains…</p>

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Custom domains</h3>
        {isAdmin && (
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus /> Add domain
          </Button>
        )}
      </header>

      {error && (
        <div className="px-4 pt-3">
          <StatusBox tone="destructive" className="text-xs">{error}</StatusBox>
        </div>
      )}

      {domains.length === 0 ? (
        <div className="flex-1 overflow-y-auto p-4">
          <EmptyState
            icon={<Globe />}
            title="No custom domains yet"
            body={
              isAdmin
                ? "Add a domain to provision a dedicated production server on your connected Hetzner account. You'll point an A record at the server's IP, and we'll handle TLS."
                : 'Only workspace owners or admins can add domains (provisioning costs the tenant money).'
            }
            action={isAdmin ? <Button size="sm" onClick={() => setShowAdd(true)}><Plus /> Add domain</Button> : undefined}
          />
        </div>
      ) : (
        <ol className="min-h-0 flex-1 divide-y overflow-y-auto">
          {domains.map((d) => {
            const variant = STATUS_VARIANT[d.status] ?? 'destructive'
            const ip = d.prodServer?.publicIpv4 ?? null
            const check = checkResults[d.id]
            return (
              <li key={d.id} className="px-4 py-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-mono text-sm text-foreground">{d.domain}</span>
                    <Badge variant={variant}>{STATUS_LABEL[d.status] ?? d.status}</Badge>
                  </div>
                  <time className="shrink-0 text-xs text-muted-foreground" dateTime={d.addedAt}>
                    added {formatRelative(d.addedAt)}
                  </time>
                </div>

                <div className="mt-2 grid gap-2 text-xs text-muted-foreground">
                  {ip ? (
                    <ARecordHint domain={d.domain} ip={ip} />
                  ) : (
                    <p>
                      Server not yet provisioned — once the IP is allocated,
                      DNS instructions will appear here.
                    </p>
                  )}

                  {d.prodServer && (
                    <p>
                      Server: <span className="font-mono text-foreground">{d.prodServer.serverType}</span> · status{' '}
                      <span className="font-mono text-foreground">{d.prodServer.status}</span>
                    </p>
                  )}

                  {d.lastDnsCheckAt && !check && (
                    <p>Last DNS check: {formatRelative(d.lastDnsCheckAt)}</p>
                  )}

                  {check && (
                    check.matched ? (
                      <StatusBox tone="success" className="text-xs">✓ DNS resolves to {check.expected}.</StatusBox>
                    ) : check.resolved ? (
                      <StatusBox tone="warning" className="text-xs">
                        DNS resolves to {check.actual.join(', ') || '(empty)'}, expected {check.expected}.
                        Update your A record and try again.
                      </StatusBox>
                    ) : (
                      <StatusBox tone="warning" className="text-xs">
                        No A record found yet. DNS propagation can take up to a few hours.
                      </StatusBox>
                    )
                  )}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {ip && d.status !== 'live' && (
                    <Button
                      variant="outline"
                      size="xs"
                      disabled={checkingId === d.id}
                      onClick={() => handleCheckDns(d.id)}
                    >
                      <RefreshCw className={checkingId === d.id ? 'animate-spin' : undefined} />
                      {checkingId === d.id ? 'Checking…' : 'Check DNS'}
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      variant="destructive"
                      size="xs"
                      disabled={removingId === d.id}
                      onClick={() => handleRemove(d)}
                    >
                      <Trash2 /> {removingId === d.id ? 'Removing…' : 'Remove & terminate server'}
                    </Button>
                  )}
                </div>

                {/* Backups + snapshots — only when there's a server to
                    snapshot. Polls Hetzner for current state on mount;
                    admins get the take-snapshot + toggle controls. */}
                {d.prodServer && (
                  <BackupsPanel
                    accessToken={accessToken}
                    serverId={d.prodServer.id}
                    isAdmin={isAdmin}
                  />
                )}
              </li>
            )
          })}
        </ol>
      )}

      {showAdd && (
        <AddDomainModal
          accessToken={accessToken}
          projectId={projectId}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false)
            setReloadKey((k) => k + 1)
          }}
        />
      )}
    </div>
  )
}

function ARecordHint({ domain, ip }: { domain: string; ip: string }) {
  // Strip the leftmost label so we suggest "@" or "www" appropriately. For
  // bare TLDs like "example.com", the host portion is "@". For subdomains
  // like "app.example.com", we suggest the actual prefix label.
  const labels = domain.split('.')
  const hostHint = labels.length > 2 ? labels[0] : '@'
  return (
    <div className="rounded-lg bg-muted p-3 font-mono text-xs">
      <p className="mb-1 font-sans font-semibold text-foreground">DNS configuration</p>
      <p className="font-sans text-muted-foreground">Add this A record at your domain registrar:</p>
      <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-foreground">
        <span className="text-muted-foreground">Type:</span>
        <span>A</span>
        <span className="text-muted-foreground">Host:</span>
        <span>{hostHint}</span>
        <span className="text-muted-foreground">Value:</span>
        <button
          type="button"
          onClick={() => navigator.clipboard.writeText(ip).catch(() => {})}
          className="inline-flex items-center gap-1 text-left text-primary hover:underline"
          title="Click to copy"
        >
          {ip} <Copy className="size-3" />
        </button>
        <span className="text-muted-foreground">TTL:</span>
        <span>300</span>
      </div>
    </div>
  )
}

function AddDomainModal({
  accessToken,
  projectId,
  onClose,
  onAdded,
}: {
  accessToken: string
  projectId: string
  onClose: () => void
  onAdded: () => void
}) {
  const [domain, setDomain] = useState('')
  const [serverTypes, setServerTypes] = useState<HetznerServerTypeDto[] | null>(null)
  const [serverType, setServerType] = useState<string>('cx23')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api.hetzner
      .listServerTypes(accessToken)
      .then((res) => {
        if (!cancelled) setServerTypes(res.types)
      })
      .catch(() => {
        // Non-fatal — user can still submit with the default cx23.
      })
    return () => {
      cancelled = true
    }
  }, [accessToken])

  const sortedTypes = useMemo(() => {
    if (!serverTypes) return null
    return [...serverTypes].sort((a, b) => a.monthlyEur - b.monthlyEur)
  }, [serverTypes])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setErr(null)
    try {
      await api.domains.add(accessToken, projectId, {
        domain: domain.trim().toLowerCase(),
        serverType,
      })
      onAdded()
    } catch (error) {
      const ae = error as ApiError
      setErr(ae.detail ?? ae.title ?? 'Failed to add domain')
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open
      onClose={() => { if (!submitting) onClose() }}
      size="md"
      title="Add a custom domain"
      description="We'll provision a dedicated production server on your connected Hetzner account and bind this domain to it. You'll be billed by Hetzner for the server until you remove the domain here."
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" form="add-domain-form" size="sm" disabled={submitting || domain.trim().length < 3}>
            {submitting ? 'Provisioning…' : 'Add & provision'}
          </Button>
        </>
      }
    >
      <form id="add-domain-form" onSubmit={handleSubmit} className="space-y-4 pt-2">
        <FloatingInput
          label="Domain"
          type="text"
          required
          autoFocus
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="example.com"
          className="font-mono"
          hint="Hostname only — no protocol, no path."
        />

        <FloatingSelect
          label="Server size"
          value={serverType}
          onChange={(e) => setServerType(e.target.value)}
        >
          {sortedTypes ? (
            sortedTypes.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name} — {t.cores}vCPU, {t.memoryGb}GB RAM, {t.diskGb}GB · €{t.monthlyEur.toFixed(2)}/mo
              </option>
            ))
          ) : (
            <option value="cx23">cx23 (default)</option>
          )}
        </FloatingSelect>

        {err && <StatusBox tone="destructive" className="text-xs">{err}</StatusBox>}
      </form>
    </Dialog>
  )
}

/**
 * Per-server backup state + controls. Surfaces:
 *   • last snapshot date (manual or automated)
 *   • how many manual snapshots vs daily backups exist
 *   • Take snapshot button (admin+)
 *   • Automated daily backups toggle (admin+, +20% server cost)
 *
 * Read-shaped GET runs for everyone; the mutating buttons only render
 * for admins (and the server-side endpoint enforces the same).
 */
function BackupsPanel({
  accessToken,
  serverId,
  isAdmin,
}: {
  accessToken: string
  serverId: string
  isAdmin: boolean
}) {
  const [snapshots, setSnapshots] = useState<HetznerSnapshotDto[] | null>(null)
  const [autoBackups, setAutoBackups] = useState<boolean | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [taking, setTaking] = useState(false)
  const [togglingBackups, setTogglingBackups] = useState(false)
  const [showSnapshots, setShowSnapshots] = useState(false)
  // Restore is destructive — wipes the disk on the server. Two-step
  // confirmation: click Restore → modal → type "RESTORE" → submit.
  // We track the in-flight image id so the UI shows a "restoring…"
  // hint on the right snapshot row.
  const [restoreTarget, setRestoreTarget] = useState<HetznerSnapshotDto | null>(null)
  const [restoringId, setRestoringId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    api.hetzner
      .listSnapshots(accessToken, serverId)
      .then((res) => {
        if (cancelled) return
        setSnapshots(res.snapshots)
        setAutoBackups(res.automatedBackupsEnabled)
        setErr(null)
      })
      .catch((e: ApiError) => {
        if (!cancelled) setErr(e.title ?? 'Failed to load backups')
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, serverId, reloadKey])

  async function handleTakeSnapshot() {
    setTaking(true)
    setErr(null)
    try {
      await api.hetzner.createSnapshot(accessToken, serverId)
      setReloadKey((k) => k + 1)
    } catch (e) {
      const ae = e as ApiError
      setErr(ae.detail ?? ae.title ?? 'Snapshot failed')
    } finally {
      setTaking(false)
    }
  }

  async function handleToggleBackups() {
    if (autoBackups === null) return
    const next = !autoBackups
    if (
      next &&
      !window.confirm(
        'Enable automated daily backups? This adds ~20% to the server\'s monthly Hetzner cost in exchange for 7 rolling daily snapshots.',
      )
    ) {
      return
    }
    setTogglingBackups(true)
    setErr(null)
    try {
      await api.hetzner.setAutomatedBackups(accessToken, serverId, next)
      setAutoBackups(next)
      setReloadKey((k) => k + 1)
    } catch (e) {
      const ae = e as ApiError
      setErr(ae.detail ?? ae.title ?? 'Backup toggle failed')
    } finally {
      setTogglingBackups(false)
    }
  }

  if (snapshots === null && err === null) {
    return (
      <p className="mt-3 text-xs text-muted-foreground">Loading backups…</p>
    )
  }

  const latest = snapshots?.[0] ?? null
  const manualCount = snapshots?.filter((s) => s.type === 'snapshot').length ?? 0
  const backupCount = snapshots?.filter((s) => s.type === 'backup').length ?? 0
  const hasCreating = snapshots?.some((s) => s.status === 'creating') ?? false

  return (
    <div className="mt-3 rounded-lg border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium text-foreground">Backups</p>
        {autoBackups !== null && (
          <Badge variant={autoBackups ? 'success' : 'outline'}>
            Daily backups {autoBackups ? 'on' : 'off'}
          </Badge>
        )}
      </div>

      <div className="mt-1.5 grid gap-0.5 text-xs text-muted-foreground">
        {latest ? (
          <p>
            Latest:{' '}
            <span className="text-foreground">
              {latest.createdAt
                ? formatRelative(latest.createdAt)
                : 'creating now'}
            </span>{' '}
            · {manualCount} manual, {backupCount} automated
          </p>
        ) : (
          <p>No snapshots yet.</p>
        )}
        {hasCreating && (
          <p className="text-warning-ink">
            A snapshot is being created — refreshes when done (~2-5 min).
          </p>
        )}
      </div>

      {err && (
        <p className="mt-1.5 text-xs text-destructive">{err}</p>
      )}

      {isAdmin && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="xs"
            disabled={taking || hasCreating}
            onClick={handleTakeSnapshot}
          >
            {taking ? 'Taking…' : 'Take snapshot'}
          </Button>
          {autoBackups !== null && (
            <Button
              variant="outline"
              size="xs"
              disabled={togglingBackups}
              onClick={handleToggleBackups}
            >
              {togglingBackups
                ? 'Updating…'
                : autoBackups
                  ? 'Disable daily backups'
                  : 'Enable daily backups (+20%)'}
            </Button>
          )}
          {snapshots && snapshots.length > 0 && (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setShowSnapshots((s) => !s)}
            >
              {showSnapshots ? 'Hide list' : `Show all (${snapshots.length})`}
            </Button>
          )}
        </div>
      )}

      {showSnapshots && snapshots && (
        <ul className="mt-2 divide-y border-t pt-1">
          {snapshots.map((s) => {
            const restorable = isAdmin && s.status === 'available'
            return (
              <li
                key={s.id}
                className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-xs"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Badge variant={s.type === 'backup' ? 'volt' : 'outline'} className="uppercase">
                    {s.type}
                  </Badge>
                  <span className="min-w-0 truncate text-foreground">
                    {s.description ?? `image #${s.id}`}
                  </span>
                  <span className="shrink-0 text-muted-foreground">
                    {s.createdAt
                      ? formatRelative(s.createdAt)
                      : s.status}
                  </span>
                </div>
                {restorable && (
                  <Button
                    variant="destructive"
                    size="xs"
                    disabled={restoringId !== null}
                    onClick={() => setRestoreTarget(s)}
                  >
                    {restoringId === s.id ? 'Restoring…' : 'Restore'}
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {restoreTarget && (
        <RestoreConfirmModal
          snapshot={restoreTarget}
          submitting={restoringId !== null}
          onCancel={() => setRestoreTarget(null)}
          onConfirm={async () => {
            setRestoringId(restoreTarget.id)
            setErr(null)
            try {
              await api.hetzner.restoreFromSnapshot(
                accessToken,
                serverId,
                restoreTarget.id,
              )
              setRestoreTarget(null)
              setReloadKey((k) => k + 1)
            } catch (e) {
              const ae = e as ApiError
              setErr(ae.detail ?? ae.title ?? 'Restore failed')
            } finally {
              setRestoringId(null)
            }
          }}
        />
      )}
    </div>
  )
}

/**
 * Two-step confirm before a destructive restore. The user has to type
 * "RESTORE" to enable the button — destructive enough that we don't
 * want a stray click on an iPad to wipe a server.
 */
function RestoreConfirmModal({
  snapshot,
  submitting,
  onCancel,
  onConfirm,
}: {
  snapshot: HetznerSnapshotDto
  submitting: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const [typed, setTyped] = useState('')
  const ready = typed === 'RESTORE'
  return (
    <Dialog
      open
      onClose={() => { if (!submitting) onCancel() }}
      size="md"
      title="Restore this snapshot?"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={onConfirm} disabled={!ready || submitting}>
            {submitting ? 'Restoring…' : 'Restore now'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <StatusBox tone="warning" className="text-xs">
          This replaces the server's disk with the snapshot from{' '}
          <strong>
            {snapshot.createdAt
              ? new Date(snapshot.createdAt).toLocaleString()
              : 'unknown time'}
          </strong>
          . Every change made on the server since then will be lost. The
          server's IP and DNS won't change — the URL stays live as soon as
          the rebuild finishes (~60-180s).
        </StatusBox>
        <FloatingInput
          label="Type RESTORE to confirm"
          type="text"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="RESTORE"
          className="font-mono"
        />
      </div>
    </Dialog>
  )
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
  if (ms < 604_800_000) return `${Math.floor(ms / 86_400_000)}d ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
